import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import attendaSvg from "/attendasvg.svg";
import { CalendarDays, CheckCircle, Bell, LogOut, Menu } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface HeaderProps {
  onMobileMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMobileMenuClick }) => {
  const { user, signOut } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Sample notifications - in a real app, these would come from an API
  const notifications = [
    { id: 1, text: 'New meeting created: Weekly CDS', time: '10m ago' },
    { id: 2, text: '5 members checked in to Monthly CDS', time: '1h ago' },
    { id: 3, text: 'New excused member added', time: '2h ago' },
  ];

  return (
    <header className="bg-white border-b border-[#D4AF37] px-4 py-3 flex justify-between items-center sticky top-0 z-50">
      <div className="flex items-center">
       
          <Menu className="h-5 w-5" />
        
        <div className="flex items-center justify-center text-white font-bold">
           <span className="text-lg font-bold text-white">
            <img 
            src={attendaSvg} 
            alt="ATTENDA" 
            className="w-32 h-full object-contain"
          />
            </span>
        </div>
        <h1 className="text-xl font-semibold text-[#D4AF37]"></h1>
      </div>
      
      {user ? (
        <div className="flex items-center space-x-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="hidden md:flex items-center text-[#D4AF37]"
          >
            <CheckCircle className="w-5 h-5 mr-1.5" />
            <span>Hi, {user.full_name}</span>
          </motion.div>
          
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Notifications"
              className="relative text-[#D4AF37] hover:text-[#BFA030] hover:bg-[#F5E6C3]/20"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell className="h-5 w-5" />
              <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full"></span>
            </Button>
            
            <AnimatePresence>
              {showNotifications && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-[#D4AF37] py-2 z-50"
                >
                  <div className="px-4 py-2 border-b border-[#D4AF37]">
                    <h3 className="font-semibold text-[#D4AF37]">Notifications</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className="px-4 py-3 hover:bg-[#F5E6C3]/20 cursor-pointer"
                      >
                        <p className="text-sm text-gray-900">{notification.text}</p>
                        <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="relative h-9 w-9 rounded-full text-[#D4AF37] hover:text-[#BFA030] hover:bg-[#F5E6C3]/20" 
                aria-label="User menu"
              >
                <Avatar className="h-9 w-9">
                  <AvatarImage src="/placeholder.svg" alt={user.full_name} />
                  <AvatarFallback>{user.full_name.charAt(0)}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 border-[#D4AF37]" align="end" forceMount>
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none text-[#D4AF37]">{user.full_name}</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="border-[#D4AF37]" />
              <DropdownMenuItem 
                onSelect={() => signOut()}
                className="text-[#D4AF37] hover:text-[#BFA030] hover:bg-[#F5E6C3]/20 cursor-pointer"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
    </header>
  );
};

export default Header;
